import React, { useState } from "react";
import Layout from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { generateFAQSchema, generateBreadcrumbSchema, generateHowToSchema } from "@/lib/schema";
import { useFairDealRates } from "@/hooks/use-fair-deal-rates";
import FairDealCalculator from "@/components/calculator/FairDealCalculator";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  ExternalLink,
  Sparkles,
  TrendingUp,
  Clock,
  Home,
  FileText,
  Users,
  ArrowRight,
  ShieldCheck,
  Calculator,
  AlertCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

// ── Static FAQs ────────────────────────────────────────────────────────────────
const FAQS = [
  {
    question: "What is the Fair Deal scheme (NHSS)?",
    answer:
      "The Nursing Homes Support Scheme (NHSS), commonly known as Fair Deal, is a government-funded programme that helps pay for long-term nursing home care in Ireland. The HSE covers the cost above your personal contribution, which is assessed based on your income and assets.",
  },
  {
    question: "Who is eligible for Fair Deal?",
    answer:
      "You must be ordinarily resident in Ireland, need long-term nursing home care, and be assessed by the HSE as requiring that level of care. There is no upper age limit and no income or asset threshold — the scheme is means-tested to calculate your contribution, not to determine eligibility.",
  },
  {
    question: "How is my Fair Deal contribution calculated?",
    answer:
      "You contribute 80% of your assessable income (e.g. pension) and 7.5% of the value of your assets per year. For your principal private residence (your home), this 7.5% contribution is capped at 3 years — after which the state covers the remaining cost. A financial assessment by the HSE determines the exact figures.",
  },
  {
    question: "Does Fair Deal cover private nursing homes?",
    answer:
      "Yes. Fair Deal covers both public HSE nursing homes and approved private and voluntary nursing homes. Private nursing homes must be HIQA-registered and have an NTPF-negotiated price to qualify for the scheme.",
  },
  {
    question: "How long does the Fair Deal application take?",
    answer:
      "The application typically takes 4–12 weeks from submission to approval. This includes the care needs assessment, financial assessment, and HSE approval. You can apply before you need immediate placement to be ready when the time comes.",
  },
  {
    question: "What happens to my home under Fair Deal?",
    answer:
      "If you own your home, 7.5% of its value per year is counted as your asset contribution — but this is capped at 3 years (22.5% total). After 3 years, the state covers the remainder. You can choose to pay the property contribution upfront or defer it (as a charge against the property) until after your death or the property is sold.",
  },
  {
    question: "Can I choose any nursing home?",
    answer:
      "You can choose any HIQA-registered nursing home that has a negotiated price with the NTPF and is participating in the Fair Deal scheme. If the nursing home charges above the NTPF-agreed rate, you may have to pay the difference ('top-up'). Grandpal lists all Fair Deal-approved nursing homes across Ireland.",
  },
  {
    question: "What is the NTPF?",
    answer:
      "The National Treatment Purchase Fund (NTPF) is the body that negotiates maximum prices with private nursing homes for Fair Deal residents. The NTPF publishes weekly rates for each nursing home, which you can find on their website or on each nursing home listing on Grandpal.",
  },
];

// ── Step-by-step guide ─────────────────────────────────────────────────────────
const STEPS = [
  {
    number: "01",
    icon: FileText,
    title: "Gather your documents",
    description:
      "Collect proof of income (pension statements, bank accounts), property valuations, and PPS numbers for yourself and your spouse.",
  },
  {
    number: "02",
    icon: Users,
    title: "Request a care needs assessment",
    description:
      "Contact your GP or local HSE office to arrange a care needs assessment. This confirms you require long-term nursing home care.",
  },
  {
    number: "03",
    icon: Calculator,
    title: "Complete the financial assessment",
    description:
      "Submit the NHSS financial assessment form (NF1) to the HSE. They calculate your contribution based on income and assets.",
  },
  {
    number: "04",
    icon: Home,
    title: "Choose a nursing home",
    description:
      "Browse HIQA-registered, Fair Deal-approved nursing homes. Use Grandpal to compare NTPF rates, amenities, and availability.",
  },
  {
    number: "05",
    icon: ShieldCheck,
    title: "Receive approval & move in",
    description:
      "Once the HSE approves your application and you have a placement, the state begins paying the balance directly to the nursing home.",
  },
];

// ── Live Rates Panel ───────────────────────────────────────────────────────────
function LiveRatesPanel() {
  const { rates, citations, isLoading, error } = useFairDealRates();

  return (
    <section
      id="current-rates"
      className="rounded-2xl border border-border bg-card p-4 sm:p-6 md:p-8 space-y-5"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary shrink-0" />
          <h2 className="text-xl sm:text-2xl font-bold text-foreground">
            Current NTPF Nursing Home Rates
          </h2>
        </div>
        <Badge variant="secondary" className="self-start sm:ml-auto text-xs">
          AI-powered · Live data
        </Badge>
      </div>

      <p className="text-muted-foreground text-sm">
        Weekly rates negotiated by the NTPF with approved nursing homes. Updated automatically using
        live sources including hse.ie, ntpf.ie, and gov.ie.
      </p>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full rounded-xl" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm p-4 rounded-lg bg-muted">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Unable to load live rates right now. Please check back shortly.</span>
        </div>
      ) : rates ? (
        <div className="space-y-5">
          {/* Key update banner */}
          {rates.keyUpdate && (
            <div className="flex items-start gap-3 p-3 sm:p-4 rounded-xl bg-primary/5 border border-primary/15">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-primary mt-0.5 shrink-0" />
              <p className="text-sm text-foreground">{rates.keyUpdate}</p>
            </div>
          )}

          {/* National average + key stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 rounded-xl bg-primary/8 border border-primary/20 p-4 sm:p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                NTPF National Average
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-primary">{rates.ntpfNationalAverage}</p>
              <p className="text-xs text-muted-foreground mt-1">per week · {rates.lastUpdated}</p>
            </div>
            <div className="rounded-xl bg-muted/50 border border-border p-3 sm:p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                State covers
              </p>
              <p className="text-lg sm:text-xl font-bold text-foreground">{rates.stateFundingPercentage}</p>
              <p className="text-xs text-muted-foreground mt-1">of cost avg</p>
            </div>
            <div className="rounded-xl bg-muted/50 border border-border p-3 sm:p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Application time
              </p>
              <p className="text-lg sm:text-xl font-bold text-foreground">{rates.averageApplicationWeeks}</p>
              <p className="text-xs text-muted-foreground mt-1">weeks typical</p>
            </div>
          </div>

          {/* Rates by region */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Weekly Rates by Region</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
              {rates.ntpfByRegion.map((r) => (
                <div
                  key={r.region}
                  className="rounded-lg border border-border bg-background p-3 sm:p-4"
                >
                  <p className="text-xs text-muted-foreground font-medium leading-tight">{r.region}</p>
                  <p className="text-sm sm:text-base font-bold text-foreground mt-1">{r.weeklyRate}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Contribution rules */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            {[
              { label: "Income contribution", value: rates.incomeContributionRate },
              { label: "Asset contribution", value: rates.assetContributionRate },
              { label: "Home asset cap", value: `${rates.propertyCapYears} years` },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border"
              >
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="text-sm font-semibold text-foreground">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Citations */}
          {citations.length > 0 && (
            <div className="pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground font-medium mb-2">Sources</p>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {citations.slice(0, 5).map((c, i) => {
                  try {
                    const domain = new URL(c).hostname.replace("www.", "");
                    return (
                      <a
                        key={i}
                        href={c}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {domain}
                      </a>
                    );
                  } catch {
                    return null;
                  }
                })}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function FairDealHubPage() {
  const currentYear = new Date().getFullYear();

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Fair Deal Scheme", url: "/fair-deal" },
  ];

  const schemas = [
    generateBreadcrumbSchema(breadcrumbs),
    generateFAQSchema(FAQS),
    generateHowToSchema({
      name: "How to Apply for the Fair Deal Scheme in Ireland",
      description:
        "A step-by-step guide to applying for Ireland's Nursing Homes Support Scheme (NHSS / Fair Deal) and finding an approved nursing home.",
      steps: STEPS.map((s) => ({ name: s.title, text: s.description })),
    }),
  ];

  return (
    <Layout>
      <SEOHead
        title={`Fair Deal Scheme Ireland ${currentYear} | Rates, Calculator & Guide | Grandpal`}
        description={`Everything about Ireland's Fair Deal (NHSS) nursing home scheme. Live NTPF rates, cost calculator, step-by-step application guide, and expert FAQs. Updated ${currentYear}.`}
        canonicalUrl="/fair-deal"
        schema={schemas}
      />

      {/* ── Hero ── */}
      <section className="bg-gradient-to-b from-primary/5 to-background py-10 sm:py-16 md:py-24 border-b border-border">
        <div className="container max-w-5xl text-center space-y-4 sm:space-y-6 px-4">
          <Badge variant="secondary" className="text-xs sm:text-sm px-3 sm:px-4 py-1">
            Ireland's definitive Fair Deal resource
          </Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground leading-tight">
            Fair Deal Scheme Ireland {currentYear}
          </h1>
          <p className="text-base sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            The Nursing Homes Support Scheme (NHSS) — commonly called Fair Deal — helps families
            afford quality nursing home care. Understand the costs, calculate your contribution, and
            find an approved home.
          </p>

          {/* Quick-link anchors */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 pt-1 sm:pt-2">
            {[
              { label: "Live NTPF Rates", href: "#current-rates" },
              { label: "Calculator", href: "#calculator" },
              { label: "Step-by-Step Guide", href: "#guide" },
              { label: "FAQs", href: "#faqs" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-border bg-background text-xs sm:text-sm font-medium hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
              >
                {link.label}
                <ArrowRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              </a>
            ))}
          </div>

          {/* Key stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 pt-4 sm:pt-8 text-left">
            {[
              { stat: "550+", label: "HIQA-approved nursing homes" },
              { stat: "4–12 wks", label: "Typical application time" },
              { stat: "7.5%", label: "Asset contribution per year" },
              { stat: "3 years", label: "Home asset cap" },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-border bg-card p-3 sm:p-4"
              >
                <p className="text-xl sm:text-2xl font-bold text-primary">{item.stat}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-tight">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="container max-w-5xl py-8 sm:py-12 md:py-16 space-y-12 sm:space-y-16 md:space-y-20 px-4">

        {/* ── Live NTPF Rates (Perplexity) ── */}
        <LiveRatesPanel />

        {/* ── Step-by-step guide ── */}
        <section id="guide">
          <div className="flex items-start gap-2 mb-6 sm:mb-8">
            <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-primary shrink-0 mt-0.5" />
            <h2 className="text-xl sm:text-2xl font-bold text-foreground">
              How to Apply for Fair Deal — Step by Step
            </h2>
          </div>

          <div className="relative">
            {/* Vertical line — desktop only */}
            <div className="absolute left-6 top-0 bottom-0 w-px bg-border hidden md:block" />

            <div className="space-y-4 sm:space-y-6">
              {STEPS.map((step) => {
                const Icon = step.icon;
                return (
                  <div key={step.number} className="flex gap-3 sm:gap-6 relative">
                    <div className="shrink-0 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-xs sm:text-sm z-10">
                      {step.number}
                    </div>
                    <div className="flex-1 bg-card border border-border rounded-xl p-3 sm:p-5 flex gap-3 sm:gap-4 items-start">
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-foreground text-sm sm:text-base">{step.title}</h3>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-1">{step.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-6 sm:mt-8 p-4 sm:p-5 rounded-xl bg-muted border border-border flex items-start gap-3">
            <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm text-foreground">
              <strong>Tip:</strong> You can begin searching for nursing homes before your assessment
              is complete. Many families find their preferred home first, then start the Fair Deal
              application — so placement is ready as soon as approval comes through.
            </div>
          </div>
        </section>

        {/* ── Calculator ── */}
        <section id="calculator">
          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <Calculator className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            <h2 className="text-xl sm:text-2xl font-bold text-foreground">
              Fair Deal Cost Calculator
            </h2>
          </div>
          <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-8">
            Estimate your personal contribution under the Fair Deal scheme. Enter your income and
            asset details below to get an instant estimate.
          </p>
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="bg-primary/5 border-b border-border px-4 sm:px-6 py-3 sm:py-4">
              <p className="text-xs sm:text-sm text-muted-foreground">
                <strong className="text-foreground">Important:</strong> This calculator provides an
                estimate only. Your actual contribution is determined by the HSE financial assessment.
                Contact a Grandpal expert for personalised guidance.
              </p>
            </div>
            <div className="p-1 sm:p-2">
              <FairDealCalculator />
            </div>
          </div>
        </section>

        {/* ── FAQs ── */}
        <section id="faqs">
          <div className="flex items-center gap-2 mb-5 sm:mb-8">
            <ShieldCheck className="h-5 w-5 sm:h-6 sm:w-6 text-primary shrink-0" />
            <h2 className="text-xl sm:text-2xl font-bold text-foreground">
              Fair Deal FAQs
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-2 sm:space-y-3">
            {FAQS.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-border rounded-xl px-3 sm:px-5 bg-card"
              >
                <AccordionTrigger className="text-left text-sm sm:text-base font-medium text-foreground hover:no-underline py-4 sm:py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-xs sm:text-sm pb-4 sm:pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* ── CTA block ── */}
        <section className="rounded-2xl bg-primary p-6 sm:p-10 text-center text-primary-foreground space-y-4 sm:space-y-5">
          <h2 className="text-xl sm:text-2xl font-bold">Ready to find a Fair Deal-approved home?</h2>
          <p className="text-sm sm:text-base text-primary-foreground/80 max-w-2xl mx-auto">
            Browse 550+ HIQA-registered nursing homes across Ireland, all filterable by Fair Deal
            eligibility, county, and amenities.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <Button
              asChild
              size="lg"
              className="bg-background text-foreground hover:bg-background/90 font-semibold w-full sm:w-auto"
            >
              <Link to="/nursing-homes">Browse Nursing Homes</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 w-full sm:w-auto"
            >
              <Link to="/consultations">Talk to an Expert</Link>
            </Button>
          </div>
        </section>

      </div>
    </Layout>
  );
}
